import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useDocument } from '@nandorojo/swr-firestore';
import { useNavigate } from 'react-router-dom';
import { CollectionPath, SettingCollectionPath } from '@typings/collections.enum';
import { Inotification } from '@typings/notification/notification.interface';
import { NOTI_TYPE } from '@typings/notification/notificationType';
import { User } from '@typings/setting/userType.interface';
import { ISoundEffect } from '@typings/setting/soundEffect.interface';
import {
  notificationSettingsState,
  lastNotificationTimeState,
  unreadNotificationsState,
} from '@atom/setting/notification';
import { homeSettingState } from '@atom/setting/homeSettingState';
import { notificationOpenState } from '@atom/setting/jukebox';
import { currentUserIDState } from '@atom/setting/user';
import { useNotificationSound } from '@hooks/useNotificationSound';
import { LOCAL_FALLBACK_URL } from '@pages/Setting/3.Homepage/Notifications/NotificationSoundPage';
import SnackBar from '@components/dataDisplay/Snackbar';
import { NotificationToastCard } from '@components/feedback/NotificationToast';
import { updateNotiRead } from '@store/firebase/notification/add-notification';
import { getSafeSenderName } from './NotificationMessageMapper';

// Persistent UX: 자동 소멸 없음. 아래 상수는 제거되었다.
// 단, 스택 최대 저장 크기는 메모리/성능 보호를 위해 제한한다.
const STACK_VISIBLE_LIMIT = 3;
const STACK_MAX_SIZE = 10;

const StackWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 260px;
  max-width: 320px;

  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
    max-width: none;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--lineColor);
`;

const OverflowRow = styled.button`
  width: 100%;
  text-align: left;
  font-size: 12px;
  color: var(--subTextColor);
  padding: 4px 0;
  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover {
    color: var(--textColor);
  }
`;

const StackToolbar = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  padding-top: 4px;
`;

const ClearAllBtn = styled.button`
  font-size: 11px;
  color: var(--subTextColor);
  background: transparent;
  border: 1px solid var(--lineColor);
  border-radius: 12px;
  cursor: pointer;
  padding: 4px 12px;
  transition: 0.15s;

  &:hover {
    color: var(--textColor);
    border-color: var(--textColor);
  }
`;

/**
 * Firestore Timestamp / JS Date / number 등 다양한 createdAt 형태를 seconds로 통일.
 */
/** HTML 태그 제거 + 대표 엔티티 디코딩 (OS 알림 body용) */
const stripHtml = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

const getCreatedAtSeconds = (notification: Inotification): number => {
  const ct = notification.createdAt as any;
  if (!ct) return 0;
  if (typeof ct.seconds === 'number') return ct.seconds;
  if (typeof ct.toMillis === 'function') return ct.toMillis() / 1000;
  if (ct instanceof Date) return ct.getTime() / 1000;
  if (typeof ct === 'number') return ct > 1e12 ? ct / 1000 : ct;
  return 0;
};

// ── Toast Stack Reducer ─────────────────────────────────────────────

type ToastEntry = {
  /** 머지 키: type + sender + collectionPath + postId */
  key: string;
  notificationId: string;
  type: string;
  senderUid: string;
  title: string;
  /** 평문 본문 (fallback) */
  body?: string;
  /** HTML 본문 (에디터 원본) - 있으면 QuillContents로 렌더링 */
  bodyHtml?: string;
  /** 본문 앞 prefix (예: "무공: ") */
  bodyPrefix?: string;
  createdAt: Inotification['createdAt'];
  navigateTo?: string;
  /** numberId 조회용 원본 정보 */
  collectionPath?: string;
  postId?: string | null;
  count: number;
};

type StackAction =
  | { type: 'PUSH'; payload: ToastEntry }
  | { type: 'DISMISS'; key: string }
  | { type: 'CLEAR' };

const buildKey = (n: Inotification): string =>
  `${n.type}|${n.sender}|${n.collectionPath || ''}|${n.postId || ''}`;

function stackReducer(state: ToastEntry[], action: StackAction): ToastEntry[] {
  switch (action.type) {
    case 'PUSH': {
      const existingIdx = state.findIndex((e) => e.key === action.payload.key);
      if (existingIdx >= 0) {
        // merge: 동일 key 존재 시 count 증가 + 최신 정보 교체
        const merged: ToastEntry = {
          ...action.payload,
          count: state[existingIdx].count + 1,
        };
        const next = state.slice();
        next.splice(existingIdx, 1);
        return [merged, ...next].slice(0, STACK_MAX_SIZE);
      }
      return [action.payload, ...state].slice(0, STACK_MAX_SIZE);
    }
    case 'DISMISS':
      return state.filter((e) => e.key !== action.key);
    case 'CLEAR':
      return [];
    default:
      return state;
  }
}

/**
 * 비채팅 알림의 인앱 토스트(스택) + 브라우저 알림을 담당하는 상시 마운트 컴포넌트.
 *
 * Persistent 정책:
 *   - 자동 소멸 없음. 유저가 카드 클릭/닫기/모두 지우기 할 때까지 유지.
 *   - 여러 알림은 스택으로 표시 (최대 3장 노출, 초과분은 overflow CTA).
 *   - 동일 (type+sender+collectionPath+postId) 조합은 1장으로 머지 + count 표기.
 *   - 카드 클릭 시 is_seen=true 처리(updateNotiRead) + 라우트 이동 + dismiss.
 */
/**
 * 단일 토스트 엔트리 래퍼: collectionPath + postId로 numberId를 조회하여
 * titlePrefix `[No.{n}] `를 구성한 뒤 NotificationToastCard로 위임한다.
 */
function NotificationToastCardEntry({
  entry,
  onClick,
  onClose,
}: {
  entry: ToastEntry;
  onClick: () => void;
  onClose: () => void;
}) {
  const { data: postData } = useDocument<any>(
    entry.collectionPath && entry.postId
      ? `${entry.collectionPath}/${entry.postId}`
      : null,
    { listen: false },
  );
  const numberId: number | undefined = postData?.numberId;
  const titlePrefix =
    typeof numberId === 'number' && numberId > 0 ? `[No.${numberId}] ` : undefined;

  return (
    <NotificationToastCard
      title={entry.title}
      titlePrefix={titlePrefix}
      body={entry.body}
      bodyHtml={entry.bodyHtml}
      bodyPrefix={entry.bodyPrefix}
      createdAt={entry.createdAt}
      count={entry.count}
      onClick={onClick}
      onClose={onClose}
    />
  );
}

function NotificationBrowserAlert() {
  const navigate = useNavigate();

  const currentUserUid = useRecoilValue(currentUserIDState);
  const notificationSettings = useRecoilValue(notificationSettingsState);
  const lastNotificationTime = useRecoilValue(lastNotificationTimeState);
  const setLastNotificationTime = useSetRecoilState(lastNotificationTimeState);
  const setNotificationOpen = useSetRecoilState(notificationOpenState);
  const homeSet = useRecoilValue(homeSettingState);
  const notifIcon = homeSet?.homepageSet?.logoImgURL || '/favicon.ico';

  // ── Toast stack ──
  const [stack, dispatchStack] = useReducer(stackReducer, [] as ToastEntry[]);

  // Firestore 알림음
  const { data: soundEffect } = useDocument<ISoundEffect>(
    SettingCollectionPath.SOUNDEFFECT,
    { listen: true },
  );
  const effectiveSoundUrl =
    soundEffect?.notificationSound?.url || LOCAL_FALLBACK_URL;

  const [prevNotificationCount, setPrevNotificationCount] = useState(0);
  const [prevLatestNotificationId, setPrevLatestNotificationId] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  const [prevCommentCount, setPrevCommentCount] = useState(0);
  const [prevLatestCommentId, setPrevLatestCommentId] = useState('');
  const isCommentInitRef = useRef(false);

  const { notify } = useNotificationSound({
    volume: notificationSettings.volume,
    soundUrl: effectiveSoundUrl,
    enableSound: notificationSettings.soundEnabled,
    enableBrowserNotification: notificationSettings.systemNotification,
  });

  const allUnreadForNotify = useRecoilValue(unreadNotificationsState);

  const unreadAllList = useMemo(() => {
    if (!allUnreadForNotify || allUnreadForNotify.length === 0) return [];
    return [...allUnreadForNotify].sort(
      (a, b) => getCreatedAtSeconds(b) - getCreatedAtSeconds(a),
    );
  }, [allUnreadForNotify]);

  const latestNonCommentSenderId = useMemo(
    () => unreadAllList.find((n) => n.type !== NOTI_TYPE.COMMENT)?.sender ?? null,
    [unreadAllList],
  );
  const { data: latestNonCommentSenderData } = useDocument<User>(
    latestNonCommentSenderId
      ? `${CollectionPath.USERS}/${latestNonCommentSenderId}`
      : null,
    { listen: false },
  );

  const unreadCommentList = useMemo(() => {
    if (!allUnreadForNotify || allUnreadForNotify.length === 0) return [];
    return allUnreadForNotify
      .filter((n) => n.type === NOTI_TYPE.COMMENT)
      .sort((a, b) => getCreatedAtSeconds(b) - getCreatedAtSeconds(a));
  }, [allUnreadForNotify]);

  const latestCommentSenderId = unreadCommentList?.[0]?.sender ?? null;
  const { data: latestCommentSenderData } = useDocument<User>(
    latestCommentSenderId
      ? `${CollectionPath.USERS}/${latestCommentSenderId}`
      : null,
    { listen: false },
  );

  // 알림 타입별 메시지 생성.
  // contents는 QuillContents 원본 HTML일 수 있으므로 bodyHtml로 분리 전달한다.
  const getNotificationMessage = useCallback(
    (
      notification: Inotification,
      senderDisplayName?: string,
    ): { title: string; body?: string; bodyHtml?: string; bodyPrefix?: string } => {
      const { type, postTitle, contents } = notification;
      const senderName = getSafeSenderName(
        senderDisplayName,
        notification.sender,
      );

      switch (type) {
        case NOTI_TYPE.COMMENT:
          return contents
            ? {
                title: '새 댓글',
                bodyHtml: contents,
                bodyPrefix: `${senderName}: `,
              }
            : {
                title: '새 댓글',
                body: `${senderName}님이 댓글을 남겼습니다.`,
              };
        case NOTI_TYPE.GUESTBOOK:
          return contents
            ? {
                title: '새 방명록',
                bodyHtml: contents,
                bodyPrefix: `${senderName}: `,
              }
            : {
                title: '새 방명록',
                body: `${senderName}님이 방명록을 작성했습니다.`,
              };
        case NOTI_TYPE.REPLY:
          return contents
            ? {
                title: '새 답글',
                bodyHtml: contents,
                bodyPrefix: `${senderName}: `,
              }
            : {
                title: '새 답글',
                body: `${senderName}님이 답글을 남겼습니다.`,
              };
        case NOTI_TYPE.TAGGED:
          return {
            title: '태그됨',
            body: postTitle
              ? `${senderName}님이 당신을 태그했습니다 — ${postTitle}`
              : `${senderName}님이 당신을 태그했습니다.`,
          };
        case NOTI_TYPE.PRESENT:
          return {
            title: '새 선물',
            body: postTitle
              ? `${senderName}님이 선물을 보냈습니다 — ${postTitle}`
              : `${senderName}님이 선물을 보냈습니다.`,
          };
        case NOTI_TYPE.BATTLE:
          return {
            title: '도전장',
            body: postTitle
              ? `${senderName}님이 도전장을 보냈습니다! — ${postTitle}`
              : `${senderName}님이 도전장을 보냈습니다!`,
          };
        case NOTI_TYPE.QUEST_REQUEST:
          return {
            title: '퀘스트 검토 요청',
            body:
              contents || `${senderName}님이 퀘스트 검토를 요청했습니다.`,
          };
        case NOTI_TYPE.DISPATCH_COMPLETE:
          return {
            title: '파견 완료',
            body: postTitle || contents || '파견이 완료되었습니다.',
          };
        default:
          return {
            title: '새 알림',
            body: `${senderName}님으로부터 새 알림이 있습니다.`,
          };
      }
    },
    [],
  );

  // ── 스낵바 푸시 + 소리 + 브라우저 알림 통합 ──
  const triggerAlert = useCallback(
    (
      notification: Inotification,
      payload: {
        title: string;
        body?: string;
        bodyHtml?: string;
        bodyPrefix?: string;
      },
      tag: string,
    ) => {
      const { title, body, bodyHtml, bodyPrefix } = payload;
      const targetRoute = notification.targetPath
        ? notification.targetPath
        : notification.collectionPath && notification.postId
          ? `/${notification.collectionPath}/${notification.postId}`
          : undefined;

      dispatchStack({
        type: 'PUSH',
        payload: {
          key: buildKey(notification),
          notificationId: notification.id || '',
          type: notification.type,
          senderUid: notification.sender,
          title,
          body,
          bodyHtml,
          bodyPrefix,
          createdAt: notification.createdAt,
          navigateTo: targetRoute,
          collectionPath: notification.collectionPath,
          postId: notification.postId,
          count: 1,
        },
      });

      // 브라우저 Notification API에는 평문만 전달 (HTML 미지원)
      const osBody = body || (bodyHtml ? stripHtml(bodyHtml) : '') || '';
      notify({
        title,
        body: bodyPrefix ? `${bodyPrefix}${osBody}` : osBody,
        tag,
        icon: notifIcon,
        badge: notifIcon,
        onClick: () => {
          if (targetRoute) navigate(targetRoute);
        },
      });
    },
    [notify, notifIcon, navigate],
  );

  // ── 일반 알림 (COMMENT 제외) 감지 ──
  useEffect(() => {
    if (!unreadAllList || unreadAllList.length === 0) return;

    const currentNotificationCount = unreadAllList.length;
    const overallLatestId = unreadAllList[0]?.id || '';

    if (!isInitialized) {
      setPrevNotificationCount(currentNotificationCount);
      setPrevLatestNotificationId(overallLatestId);
      setIsInitialized(true);

      if (lastNotificationTime === 0) {
        setLastNotificationTime(Date.now());
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 알림 시스템 초기화:', {
          initialCount: currentNotificationCount,
          latestId: overallLatestId.substring(0, 8) + '...',
        });
      }
      return;
    }

    const hasNewNotifications =
      currentNotificationCount > prevNotificationCount;
    const hasNewLatestNotification =
      overallLatestId && overallLatestId !== prevLatestNotificationId;

    if (!hasNewNotifications && !hasNewLatestNotification) {
      setPrevNotificationCount(currentNotificationCount);
      setPrevLatestNotificationId(overallLatestId);
      return;
    }

    const targetNotification = unreadAllList.find(
      (n) => n.type !== NOTI_TYPE.COMMENT,
    );

    if (targetNotification) {
      const targetId = targetNotification.id || '';
      const lastShownId =
        localStorage.getItem('last_shown_notification_id') || '';

      if (targetId && targetId !== lastShownId) {
        const typeSettingMap: Partial<Record<string, boolean>> = {
          [NOTI_TYPE.REPLY]: notificationSettings.replyNotification,
          [NOTI_TYPE.GUESTBOOK]: notificationSettings.guestbookNotification,
          [NOTI_TYPE.TAGGED]: notificationSettings.taggedNotification,
          [NOTI_TYPE.PRESENT]: notificationSettings.giftNotification,
          [NOTI_TYPE.BATTLE]: notificationSettings.battleNotification,
          [NOTI_TYPE.QUEST_REQUEST]: notificationSettings.questNotification,
          [NOTI_TYPE.DISPATCH_COMPLETE]: notificationSettings.dispatchNotification,
        };
        const typeAllowed = typeSettingMap[targetNotification.type] ?? true;

        const now = Date.now();
        const timeSinceLastNotification = now - lastNotificationTime;
        if (timeSinceLastNotification > 1000) {
          if (typeAllowed) {
            const reuseName = latestNonCommentSenderData?.displayName;
            const msg = getNotificationMessage(
              targetNotification,
              reuseName,
            );
            triggerAlert(
              targetNotification,
              msg,
              `notification-${targetNotification.type}-${targetId}`,
            );
            setLastNotificationTime(now);
          }
          try {
            localStorage.setItem('last_shown_notification_id', targetId);
          } catch {}
        }
      }
    }

    setPrevNotificationCount(currentNotificationCount);
    setPrevLatestNotificationId(overallLatestId);
  }, [
    unreadAllList,
    isInitialized,
    prevNotificationCount,
    prevLatestNotificationId,
    lastNotificationTime,
    setLastNotificationTime,
    getNotificationMessage,
    triggerAlert,
    latestNonCommentSenderData,
    notificationSettings.guestbookNotification,
    notificationSettings.replyNotification,
    notificationSettings.taggedNotification,
    notificationSettings.giftNotification,
    notificationSettings.chatNotification,
    notificationSettings.battleNotification,
    notificationSettings.questNotification,
    notificationSettings.dispatchNotification,
  ]);

  // ── 댓글 알림 전용 감지 ──
  useEffect(() => {
    if (!unreadCommentList || unreadCommentList.length === 0) return;

    const currentCount = unreadCommentList.length;
    const latestComment = unreadCommentList[0];
    const currentLatestId = latestComment?.id || '';

    if (!isCommentInitRef.current) {
      isCommentInitRef.current = true;
      setPrevCommentCount(currentCount);
      setPrevLatestCommentId(currentLatestId);
      return;
    }

    const lastShownCommentId =
      localStorage.getItem('last_shown_comment_id') || '';

    const hasNew =
      currentCount > prevCommentCount ||
      currentLatestId !== prevLatestCommentId;

    if (hasNew && latestComment && currentLatestId !== lastShownCommentId) {
      const now = Date.now();
      const timeSinceLast = now - lastNotificationTime;
      if (timeSinceLast > 1000) {
        if (notificationSettings.commentNotification) {
          const reuseName = latestCommentSenderData?.displayName;
          const msg = getNotificationMessage(
            latestComment,
            reuseName,
          );
          triggerAlert(
            latestComment,
            msg,
            `comment-${currentLatestId}`,
          );
          setLastNotificationTime(now);
        }
        try {
          localStorage.setItem('last_shown_comment_id', currentLatestId);
        } catch {}
      }
    }

    setPrevCommentCount(currentCount);
    setPrevLatestCommentId(currentLatestId);
  }, [
    unreadCommentList,
    lastNotificationTime,
    latestCommentSenderData,
    prevCommentCount,
    prevLatestCommentId,
    getNotificationMessage,
    triggerAlert,
    setLastNotificationTime,
    notificationSettings.commentNotification,
  ]);

  // ── 카드 인터랙션 ──
  const handleCardClick = useCallback(
    (entry: ToastEntry) => {
      // is_seen 처리 + 라우트 이동 + dismiss
      if (currentUserUid && entry.notificationId) {
        updateNotiRead({
          userId: currentUserUid,
          notificationID: entry.notificationId,
        }).catch(() => {});
      }
      if (entry.navigateTo) {
        navigate(entry.navigateTo);
      }
      dispatchStack({ type: 'DISMISS', key: entry.key });
    },
    [currentUserUid, navigate],
  );

  const handleCardClose = useCallback((entry: ToastEntry) => {
    // 스낵바만 닫기 (is_seen 유지 → 알림 리스트에서 다시 확인 가능)
    dispatchStack({ type: 'DISMISS', key: entry.key });
  }, []);

  const handleClearAll = useCallback(() => {
    dispatchStack({ type: 'CLEAR' });
  }, []);

  const handleOpenPanel = useCallback(() => {
    setNotificationOpen(true);
    dispatchStack({ type: 'CLEAR' });
  }, [setNotificationOpen]);

  const showStack = stack.length > 0;
  const visibleEntries = stack.slice(0, STACK_VISIBLE_LIMIT);
  const overflowCount = Math.max(0, stack.length - STACK_VISIBLE_LIMIT);

  // SnackBar는 단일 show 스위치만 쓰므로, 스택이 비면 자연 언마운트.
  // noDelay + show={true} 조합으로 persistent 유지.
  return (
    <SnackBar
      show={showStack}
      setShow={(v: boolean) => {
        if (!v) dispatchStack({ type: 'CLEAR' });
      }}
      noDelay
    >
      <StackWrap>
        {visibleEntries.map((entry, idx) => (
          <React.Fragment key={entry.key}>
            {idx > 0 && <Divider />}
            <NotificationToastCardEntry
              entry={entry}
              onClick={() => handleCardClick(entry)}
              onClose={() => handleCardClose(entry)}
            />
          </React.Fragment>
        ))}
        {overflowCount > 0 && (
          <>
            <Divider />
            <OverflowRow onClick={handleOpenPanel}>
              외 {overflowCount}개 알림 보기
            </OverflowRow>
          </>
        )}
        {stack.length > 1 && (
          <StackToolbar>
            <ClearAllBtn onClick={handleClearAll}>모두 지우기</ClearAllBtn>
          </StackToolbar>
        )}
      </StackWrap>
    </SnackBar>
  );
}

export default NotificationBrowserAlert;
